import { useEffect, useState } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router';
import { isDesktop } from '../lib/platform';

/** Height covered by the on-screen keyboard (iOS does not resize the layout viewport). */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      setInset(covered > 80 ? covered : 0);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}

/** Sets the toast offset (above the tab bar or a sticky Save bar) while mounted. */
export function useToastOffset(px: number) {
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.getPropertyValue('--toast-bottom');
    root.style.setProperty('--toast-bottom', `${px}px`);
    return () => {
      if (prev) root.style.setProperty('--toast-bottom', prev);
      else root.style.removeProperty('--toast-bottom');
    };
  }, [px]);
}

export interface FlowState {
  background?: Location;
}

/**
 * Router state for links that open a flow (add expense, settle up, settings, new group…): on
 * desktop the flow opens as a dialog over the current page (DESIGN 3.3).
 */
export function useFlowState(): FlowState | undefined {
  const location = useLocation();
  const current = location.state as FlowState | null;
  if (!isDesktop()) return undefined;
  return { background: current?.background ?? location };
}

/** Close a flow: go back if we came from inside the app, else to `fallback`. */
export function useCloseFlow(fallback: string) {
  const navigate = useNavigate();
  const location = useLocation();
  return (opts?: { replaceWith?: string }) => {
    if (opts?.replaceWith) {
      void navigate(opts.replaceWith, { replace: true });
      return;
    }
    if (location.key !== 'default' && window.history.length > 1) void navigate(-1);
    else void navigate(fallback, { replace: true });
  };
}

const LAST_GROUP_KEY = 'split.lastGroup';
export function rememberLastGroup(id: string) {
  try {
    localStorage.setItem(LAST_GROUP_KEY, id);
  } catch {
    // ignore
  }
}
export function lastGroup(): string | null {
  try {
    return localStorage.getItem(LAST_GROUP_KEY);
  } catch {
    return null;
  }
}

/** `returnTo` survives sign-in / onboarding (DESIGN 4.8, 4.18.4). */
const RETURN_KEY = 'split.returnTo';
export function setReturnTo(path: string) {
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // ignore
  }
}
export function takeReturnTo(): string | null {
  try {
    const v = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null;
  } catch {
    return null;
  }
}
